import { add } from 'date-fns';
import { noop } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { CreditSummary } from '~/features/billing/CreditBalance/types';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

interface FormState {
  currentRecurringCredits: Nullable<number>;
  newRecurringCredits: Nullable<number>;
  effectiveDate: Nullable<Date>;
  renewalDate: Nullable<Date>;
  notes: Nullable<string>;
}

interface Props {
  creditSummary: Nullable<CreditSummary>;
  onClose: () => void;
}

const initialFormState = {
  currentRecurringCredits: 0,
  newRecurringCredits: 0,
  effectiveDate: null,
  renewalDate: add(new Date(), { years: 1 }),
  notes: '',
};

export const RecurringCreditsPanel = ({ creditSummary, onClose }: Props) => {
  const intl = useIntl();
  const [id, updateId] = useUuid();
  const { RETRY_ERROR } = useErrors();
  const { params } = useRouting();

  const [updateRecurringCredits, { isLoading }] =
    api.endpoints.updateRecurringCredits.useMutation();
  const [formState, setFormState] = useState<FormState>(
    creditSummary && creditSummary.recurringCredits
      ? {
          ...initialFormState,
          currentRecurringCredits: creditSummary.recurringCredits.credits,
          renewalDate: creditSummary.recurringCredits.renewalDate,
          notes: creditSummary.recurringCredits.notes,
        }
      : initialFormState,
  );

  const { toast } = useToast();

  const {
    currentRecurringCredits,
    newRecurringCredits,
    effectiveDate,
    renewalDate,
    notes,
  } = formState;

  const disableAddCreditsButton =
    newRecurringCredits! <= 0 || effectiveDate === null;

  return (
    <Panel
      footer={{
        actions: [
          {
            level: 'tertiary' as const,
            text: intl.formatMessage({
              id: 'RecurringCreditsPanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: onClose,
          },
          {
            disabled: disableAddCreditsButton,
            isLoading,
            level: 'primary' as const,
            text: intl.formatMessage({
              id: 'RecurringCreditsPanel.addCreditsButton',
              defaultMessage: 'Add Credits',
              description: 'Button text to add recurring credits',
            }),
            tooltip: disableAddCreditsButton
              ? intl.formatMessage({
                  id: 'RecurringCreditsPanel.fieldsValidationTooltip',
                  defaultMessage:
                    'Please make sure all fields are filled correctly',
                  description: 'Tooltip when form fields are invalid',
                })
              : '',
            onClick: () => {
              updateRecurringCredits({
                clientId: Number(params.clientId),
                credits: newRecurringCredits!,
                effectiveDate: effectiveDate!,
                idempotencyKey: `recurring_${id}`,
                notes: notes!,
              })
                .unwrap()
                .then((data) => {
                  if (data) {
                    toast({
                      message: intl.formatMessage({
                        id: 'RecurringCreditsPanel.creditsAddedSuccess',
                        defaultMessage: 'Credits added successfully',
                        description:
                          'Success message when recurring credits are added',
                      }),
                      status: 'success',
                    });
                    onClose();
                  }
                })
                .catch((err) => {
                  toast({
                    message: err[0].detail ?? RETRY_ERROR,
                    status: 'danger',
                  });
                });
            },
          },
        ],
      }}
      hidden={{
        onHide: onClose,
      }}
      position="right"
      title={intl.formatMessage({
        id: 'RecurringCreditsPanel.panelTitle',
        defaultMessage: 'Manage Recurring Credits',
        description: 'Panel title for managing recurring credits',
      })}
      width="m"
    >
      <Layout preset="form-fields">
        <FormField<number, false>
          disabled={true}
          input={NumberInput}
          inputProps={{
            min: 1,
            max: 1_000_000_000, // 1 billion per product requirement
          }}
          label={intl.formatMessage({
            id: 'RecurringCreditsPanel.currentRecurringCreditAmountLabel',
            defaultMessage: 'Current Recurring Credit Amount',
            description: 'Label for current recurring credit amount field',
          })}
          name="Current Recurring Credit Amount"
          value={currentRecurringCredits}
          onChange={noop}
        />
        <FormField<number, false>
          required
          input={NumberInput}
          inputProps={{
            min: 1,
          }}
          label={intl.formatMessage({
            id: 'RecurringCreditsPanel.newRecurringCreditAmountLabel',
            defaultMessage: 'New Recurring Credit Amount',
            description: 'Label for new recurring credit amount field',
          })}
          name="New Recurring Credit Amount"
          value={newRecurringCredits}
          onChange={(newRecurringCredits) => {
            updateId();
            setFormState({
              ...formState,
              newRecurringCredits,
            });
          }}
        />
        <FormField<Date, false>
          required
          input={DateInput}
          label={intl.formatMessage({
            id: 'RecurringCreditsPanel.effectiveDateLabel',
            defaultMessage: 'Effective Date',
            description: 'Label for effective date field',
          })}
          name="Effective Date"
          value={effectiveDate}
          onChange={(effectiveDate) => {
            updateId();
            setFormState({
              ...formState,
              effectiveDate,
              renewalDate: effectiveDate
                ? add(effectiveDate, { years: 1 })
                : creditSummary?.recurringCredits?.renewalDate
                  ? new Date(creditSummary.recurringCredits.renewalDate)
                  : add(new Date(), { years: 1 }),
            });
          }}
        />
        <FormField<Date, false>
          disabled={true}
          input={DateInput}
          label={intl.formatMessage({
            id: 'RecurringCreditsPanel.renewalDateLabel',
            defaultMessage: 'Renewal Date',
            description: 'Label for renewal date field',
          })}
          name="Renewal Date"
          width="100%"
          value={renewalDate}
          onChange={noop}
        />
        <FormField<string, false>
          footer={intl.formatMessage({
            id: 'RecurringCreditsPanel.notesMaxCharactersFooter',
            defaultMessage: 'Maximum 300 characters',
            description: 'Footer text for notes field character limit',
          })}
          input={TextArea}
          inputProps={{
            maxLength: 300,
          }}
          label={intl.formatMessage({
            id: 'RecurringCreditsPanel.notesLabel',
            defaultMessage: 'Notes',
            description: 'Label for notes field',
          })}
          maxLength={300}
          name="Notes"
          value={notes}
          onChange={(notes) => {
            updateId();
            setFormState({
              ...formState,
              notes,
            });
          }}
        />
      </Layout>
    </Panel>
  );
};
